import { amostrasDoTom, type Tom } from '@/utils/audio/tomDeLigacao';

/**
 * Toca os tons de ligação: o "chamando" enquanto a discagem do operador toca,
 * e os três bipes quando a ligação acaba. A forma de onda é de
 * `utils/audio/tomDeLigacao`.
 *
 * `AudioContext` PRÓPRIO, e não o da `LigacaoAudio`: aquele nasce e morre com a
 * sessão de áudio de uma ligação — e o fim toca justamente depois de ela
 * fechar. Um só para o app inteiro, criado no primeiro uso.
 *
 * Volume: o do sistema/aba. Não segue as preferências de som de mensagem
 * (`soundStore`) de propósito — o "chamando" é o único sinal de que a discagem
 * está viva, e desligá-lo junto com o som de mensagem nova deixaria o operador
 * sem saber se a ligação saiu.
 */

let contexto: AudioContext | null = null;
const buffers = new Map<Tom, AudioBuffer>();
let chamando: AudioBufferSourceNode | null = null;

function obterContexto(): AudioContext | null {
    if (contexto) return contexto;
    const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return null;
    contexto = new Ctor();
    return contexto;
}

/**
 * Cria e destrava o contexto. Chame de forma SÍNCRONA no gesto do operador
 * (clique em discar/atender), antes de qualquer `await`: o Safari cria o
 * `AudioContext` suspenso e só aceita o `resume` dentro do gesto — fora dele o
 * "chamando" e o fim ficariam mudos. O Chrome também aceita depois, por ter
 * havido interação com a página, mas não custa nada pedir no momento certo.
 */
export function prepararTons(): void {
    const ac = obterContexto();
    if (ac?.state === 'suspended') destravar(ac);
}

function destravar(ac: AudioContext): void {
    ac.resume().catch(e => console.warn('[LIGACAO] o navegador não liberou o som dos tons', e));
}

function bufferDo(ac: AudioContext, tom: Tom): AudioBuffer {
    const pronto = buffers.get(tom);
    if (pronto) return pronto;
    const amostras = amostrasDoTom(tom, ac.sampleRate);
    const buffer = ac.createBuffer(1, amostras.length, ac.sampleRate);
    buffer.copyToChannel(amostras, 0);
    buffers.set(tom, buffer);
    return buffer;
}

function tocar(tom: Tom, emLaco: boolean): AudioBufferSourceNode | null {
    const ac = obterContexto();
    if (!ac) return null;
    try {
        // Suspenso pela política de autoplay. `prepararTons` já pediu no
        // gesto; isto é o reforço para quem chega aqui sem ele (fim remoto).
        if (ac.state === 'suspended') destravar(ac);
        const fonte = ac.createBufferSource();
        fonte.buffer = bufferDo(ac, tom);
        fonte.loop = emLaco;
        fonte.connect(ac.destination);
        fonte.start();
        return fonte;
    } catch (e) {
        // Tom é aviso, não a ligação: falhar aqui não pode derrubar o card.
        console.warn('[LIGACAO] não consegui tocar o tom', tom, e);
        return null;
    }
}

/** Liga ou desliga o "chamando". Idempotente: o store chama a cada mudança. */
export function definirChamando(ligado: boolean): void {
    if (ligado === (chamando !== null)) return;
    if (!ligado) {
        try {
            chamando?.stop();
        } catch {
            // `stop` de fonte que já parou lança InvalidStateError — nada a fazer.
        }
        chamando?.disconnect();
        chamando = null;
        return;
    }
    chamando = tocar('chamando', true);
}

/** Três bipes de fim de ligação. Corta o "chamando", se ainda estiver tocando. */
export function tocarFim(): void {
    definirChamando(false);
    tocar('fim', false);
}
